// src/components/circles/LeaveCircle.jsx
//
// The quiet exit, for everyone in the circle - including whoever started it,
// who used to be stuck: the old leave tried to hand the circle on to someone
// else, the rules refused, and nothing said so. There is nothing to hand on
// now, because nobody owns a circle.
//
// Leaving takes your name and your answers with you. Nobody is told. The last
// one out closes the circle, so an empty one never sits there with a live
// invite code.

import { useState } from 'react';
import { LogOut } from 'lucide-react';

export default function LeaveCircle({ circle, onLeave, label = 'Leave this circle' }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const lastOneIn = (circle?.members || []).length <= 1;

  const leave = async () => {
    setBusy(true);
    setError('');
    const res = await onLeave();
    setBusy(false);
    if (!res?.success) setError("That didn't work. Check your connection and try again.");
  };

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="inline-flex items-center gap-2 text-sm text-white/70 underline underline-offset-4 hover:text-white transition-colors"
      >
        <LogOut aria-hidden="true" className="w-4 h-4" />
        {label}
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-white/15 bg-white/5 p-4 text-left">
      <p className="text-sm text-white">Leave {circle?.name || 'this circle'}?</p>
      <p className="mt-2 text-sm text-white/70">
        {lastOneIn
          ? 'You’re the last one here, so leaving closes the circle for good and its code stops working.'
          : 'Your name and your answers in this circle go with you. Nobody is told, and you can rejoin later with the code if you want to.'}
      </p>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={leave}
          disabled={busy}
          className="px-4 py-2.5 bg-white/15 hover:bg-white/25 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
        >
          {busy ? 'Leaving…' : lastOneIn ? 'Leave and close it' : 'Leave'}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          disabled={busy}
          className="px-4 py-2.5 bg-transparent hover:bg-white/10 text-white/80 rounded-lg text-sm border border-white/15"
        >
          Stay
        </button>
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-red-200">{error}</p>}
    </div>
  );
}
